import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { ProcedureSearch, amt, round2, lineMoney } from "@/components/crm/pickers";

/* The invoice's line items, in the shape the patient chart already uses.
 *
 * Billing was the odd one out: the chart prices a visit off the catalogue and
 * the invoice asked reception to retype the name into "Service / treatment"
 * and remember the price. Same clinic, same price list, two different ways of
 * entering it — and the one that fed the money was the one working from
 * memory. This is that table, with the catalogue behind the same search.
 *
 * On discount. Each line carries its own discount (₹ or %), sent as-is and
 * applied by the server BEFORE tax, so the patient is not taxed on money they
 * did not pay — and the bill can show Total cost / Total discount the way
 * Practo does, instead of a quietly reduced rate. The invoice-level discount
 * below is a different instrument — it comes off the whole bill after tax.
 */

// Tax is opt-in, as in Practo: a blank means "None". Consultations and most
// procedures carry no GST, and an 18% default overcharged anyone who missed it.
// A catalogue item with its own tax_percent still brings it along.

let lineKey = 0;

export function invoiceRow(seed = {}) {
  return {
    key: `line-${++lineKey}`,
    package_id: seed.package_id || "",
    description: seed.name || seed.description || "",
    quantity: seed.quantity ?? 1,
    rate: seed.rate ?? "",
    discount: seed.discount || "",
    discount_type: "amount",
    // GST is the clinic's default unless the catalogue says otherwise. A blank
    // from an ad-hoc line means "the usual", not "exempt".
    tax_percent: seed.tax_percent == null || Number(seed.tax_percent) === 0
      ? "" : seed.tax_percent,
  };
}

/* One line as the SERVER will store it — mirrors _apply_item_tax and
 * _compute_totals so the desk sees the same paisa the printout will. */
export function billedLine(r) {
  const quantity = Math.max(1, Math.floor(Number(r.quantity) || 1));
  const m = lineMoney({ ...r, quantity });
  const taxPct = Number(r.tax_percent) || 0;
  const tax = round2(m.net * taxPct / 100);
  return { quantity, rate: Number(r.rate) || 0, amount: m.net, tax,
           total: round2(m.net + tax), gross: m.gross, discount: m.discount };
}

const named = (rows) => rows.filter((r) => (r.description || "").trim());

/* What the rows mean to POST /invoices. A % discount travels as the percent
 * (the server recomputes the ₹ from it and prints "(50%)"); a ₹ one as is. */
export function toInvoiceItems(rows) {
  return named(rows).map((r) => {
    const b = billedLine(r);
    const pct = r.discount_type === "percent" && Number(r.discount) > 0
      ? Number(r.discount) : null;
    return {
      description: r.description.trim(),
      quantity: b.quantity,
      rate: b.rate,
      discount: b.discount,
      discount_percent: pct,
      tax_percent: Number(r.tax_percent) || 0,
    };
  });
}

export function invoiceTotals(rows, discount = 0) {
  const sums = named(rows).reduce((s, r) => {
    const b = billedLine(r);
    return { cost: round2(s.cost + b.gross), discount: round2(s.discount + b.discount),
             subtotal: round2(s.subtotal + b.amount), tax: round2(s.tax + b.tax) };
  }, { cost: 0, discount: 0, subtotal: 0, tax: 0 });
  return {
    ...sums,
    grand: round2(Math.max(0, sums.subtotal + sums.tax - (Number(discount) || 0))),
  };
}

export function InvoiceLineItems({ rows, onChange }) {
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => setCatalog(r.data || [])).catch(() => {});
  }, []);

  const setRow = (i, patch) =>
    onChange(rows.map((r, ix) => (ix === i ? { ...r, ...patch } : r)));

  return (
    <div className="inv-rows">
      <div className="inv-head border-b border-navy/10 px-1 pb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <span>Procedure</span><span>Unit * Cost(₹)</span><span>Discount</span>
        <span>Tax %</span><span className="inv-amount">Total(₹)</span><span className="w-5" />
      </div>

      {rows.map((r, i) => (
        <InvoiceLineRow key={r.key} row={r}
                        onChange={(patch) => setRow(i, patch)}
                        onRemove={() => onChange(rows.filter((_, ix) => ix !== i))} />
      ))}

      {!rows.length && (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Nothing on this bill yet — find the procedure below.
        </p>
      )}

      <div className="pt-3">
        <ProcedureSearch catalog={catalog} onPick={(seed) => onChange([...rows, invoiceRow(seed)])} />
      </div>
    </div>
  );
}

function InvoiceLineRow({ row: r, onChange, onRemove }) {
  const b = billedLine(r);
  return (
    <div className="inv-row border-b border-navy/5 py-2.5 last:border-b-0" data-testid="invoice-line-row">
      <div className="min-w-0">
        <Input value={r.description || ""} placeholder="Procedure name"
               onChange={(e) => onChange({ description: e.target.value })}
               className="h-7 border-0 bg-transparent px-0 text-sm text-ink shadow-none focus-visible:ring-0" />
      </div>

      <div className="min-w-0">
        <span className="inv-label">Unit * Cost(₹)</span>
        <div className="flex items-center gap-1.5">
          <Input className="h-8 w-14 shrink-0 bg-white text-center" type="number" min="1" step="1"
                 value={r.quantity ?? 1} onChange={(e) => onChange({ quantity: e.target.value })} />
          <Input className="h-8 min-w-0 flex-1 bg-white" type="number" min="0" placeholder="0"
                 value={r.rate ?? ""} onChange={(e) => onChange({ rate: e.target.value })} />
        </div>
      </div>

      <div className="min-w-0">
        <span className="inv-label">Discount</span>
        <div className="flex items-center gap-1.5">
          <Input className="h-8 min-w-0 flex-1 bg-white" type="number" min="0" placeholder="0"
                 value={r.discount ?? ""} onChange={(e) => onChange({ discount: e.target.value })} />
          <Select value={r.discount_type || "amount"}
                  onValueChange={(v) => onChange({ discount_type: v })}>
            <SelectTrigger className="h-8 w-16 shrink-0 bg-white text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="amount">₹</SelectItem>
              <SelectItem value="percent">%</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {r.discount_type === "percent" && b.discount > 0 && (
          <p className="mt-0.5 text-[11px] text-muted-foreground">= ₹{amt(b.discount)}</p>
        )}
      </div>

      <div className="min-w-0">
        <span className="inv-label">Tax %</span>
        <Input className="h-8 w-16 bg-white text-center" type="number" min="0" placeholder="None"
               value={r.tax_percent ?? ""}
               onChange={(e) => onChange({ tax_percent: e.target.value })} />
      </div>

      <div className="inv-amount min-w-0">
        <span className="inv-label">Total(₹)</span>
        <p className="pt-1.5 text-sm font-medium text-ink" data-testid="invoice-line-total">{amt(b.total)}</p>
      </div>

      <button onClick={onRemove} title="Remove" type="button"
              className="justify-self-start rounded p-1 text-red-500 hover:bg-red-50">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/* The figures under the table, in the order the desk reads them out: what the
 * work cost, what came off it, the tax, and the number to ask for. */
export function InvoiceTotals({ rows, discount, onDiscount }) {
  const t = invoiceTotals(rows, discount);
  return (
    <div className="rounded-lg bg-cream/60 p-3 text-sm">
      <Row label="Total cost" value={amt(t.cost)} />
      <Row label="Total discount" value={t.discount > 0 ? `− ${amt(t.discount)}` : amt(0)} />
      <Row label="Total tax" value={amt(t.tax)} />
      <div className="flex items-center justify-between py-0.5">
        <span className="text-muted-foreground">Discount on the bill</span>
        <Input type="number" min="0" value={discount} data-testid="invoice-discount"
               onChange={(e) => onDiscount(e.target.value)}
               className="h-7 w-28 bg-white text-right text-xs" />
      </div>
      <div className="mt-1 flex justify-between border-t border-navy/10 pt-1.5 font-display text-base font-semibold text-navy">
        <span>Grand total</span>
        <span data-testid="invoice-grand-total">₹{amt(t.grand)}</span>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Payment — Practo's "Accept Payment" block under the bill.
 *
 * `value` is { method, useCredit, payNow, reference, creditBalance, split }.
 * payNow left blank means "whatever is still owed after the advance and any
 * other modes", which is what the desk wants nine times in ten; typing a
 * smaller figure leaves a balance. `split` holds the other modes of a bill
 * paid more than one way ({ method, amount, reference } each) — type ₹500 on
 * the UPI row and the card row fills with the rest. resolvePayment() is the
 * one place that turns it into numbers, so the screen and the request body
 * cannot disagree.
 * ------------------------------------------------------------------------- */
export const emptyPayment = () =>
  ({ method: "cash", useCredit: "", payNow: "", reference: "", creditBalance: 0, split: [] });

export function resolvePayment(value, grand) {
  const payable = round2(Math.max(0, Number(grand) || 0));
  const creditAvail = round2(Math.min(Number(value.creditBalance) || 0, payable));
  const fromAdvance = round2(Math.min(Math.max(0, Number(value.useCredit) || 0), creditAvail));
  let room = round2(payable - fromAdvance);
  // Other modes first, each capped at what is still owed, so the main row's
  // blank default is exactly the remainder.
  const split = (value.split || []).map((t) => {
    const amount = round2(Math.min(Math.max(0, Number(t.amount) || 0), room));
    room = round2(room - amount);
    return { ...t, amount };
  });
  const main = value.payNow === "" || value.payNow == null
    ? room : round2(Math.min(Math.max(0, Number(value.payNow) || 0), room));
  const payNow = round2(main + split.reduce((sum, t) => sum + t.amount, 0));
  return { payable, creditAvail, fromAdvance, main, split, payNow,
           balance: round2(room - main), collected: round2(fromAdvance + payNow) };
}

/* The `payment` body for POST /invoices, or null when nothing is being taken. */
export function paymentBody(value, grand) {
  const r = resolvePayment(value, grand);
  if (r.fromAdvance <= 0 && r.payNow <= 0) return null;
  return { amount: r.main, method: value.method || "cash",
           reference: value.reference || "", use_credit: r.fromAdvance,
           split: r.split.filter((t) => t.amount > 0)
             .map((t) => ({ amount: t.amount, method: t.method || "cash", reference: t.reference || "" })) };
}

export function InvoicePayment({ patientId, grand, value, onChange }) {
  const [modes, setModes] = useState([]);
  const set = (patch) => onChange({ ...value, ...patch });

  useEffect(() => {
    api.get("/payment-modes").then((r) => setModes(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!patientId) return;
    api.get(`/patients/${patientId}/credit`)
      .then((r) => onChange((v) => ({ ...v, creditBalance: r.data?.balance || 0 })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const r = resolvePayment(value, grand);
  const modeNames = modes.length ? modes.filter((m) => m.active !== false).map((m) => m.name)
                                 : ["cash", "card", "upi"];
  const split = value.split || [];
  const setSplit = (i, patch) => set({ split: split.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  // A second mode is almost never the same as the first; default to the next one.
  const addSplit = () => set({ split: [...split, {
    method: modeNames.find((m) => m !== value.method && !split.some((t) => t.method === m)) || modeNames[0],
    amount: "", reference: "" }] });

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm" data-testid="invoice-payment">
      <p className="mb-2 font-display font-semibold text-navy">Payment</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Mode of payment</span>
          <Select value={value.method || "cash"} onValueChange={(v) => set({ method: v })}>
            <SelectTrigger className="mt-0.5 h-8 bg-white text-xs" data-testid="payment-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              {modeNames.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Payable (₹)</span>
          <p className="pt-1.5 font-medium text-ink">{amt(r.payable)}</p>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">From advance (₹)</span>
          <Input className="mt-0.5 h-8 bg-white" type="number" min="0" placeholder="0"
                 disabled={r.creditAvail <= 0} value={value.useCredit}
                 onChange={(e) => set({ useCredit: e.target.value })} data-testid="payment-from-advance" />
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {r.creditAvail > 0
              ? <button type="button" className="underline" onClick={() => set({ useCredit: String(r.creditAvail) })}>
                  ₹{amt(Number(value.creditBalance) || 0)} available — use it
                </button>
              : "No advance on account"}
          </p>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Pay now (₹)</span>
          <Input className="mt-0.5 h-8 bg-white" type="number" min="0"
                 placeholder={amt(r.main)} value={value.payNow}
                 onChange={(e) => set({ payNow: e.target.value })} data-testid="payment-pay-now" />
        </div>
      </div>
      {value.method && value.method !== "cash" && (
        <Input className="mt-2 h-8 bg-white text-xs" placeholder="Reference (UPI / card / cheque no.) — optional"
               value={value.reference || ""} onChange={(e) => set({ reference: e.target.value })} />
      )}

      {split.map((t, i) => (
        <div key={i} className="mt-2 grid grid-cols-[1fr_1fr_2fr_auto] items-center gap-2" data-testid={`payment-split-${i}`}>
          <Select value={t.method} onValueChange={(v) => setSplit(i, { method: v })}>
            <SelectTrigger className="h-8 bg-white text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {modeNames.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="h-8 bg-white" type="number" min="0" placeholder="Amount (₹)" value={t.amount}
                 onChange={(e) => setSplit(i, { amount: e.target.value })} data-testid={`payment-split-amount-${i}`} />
          <Input className="h-8 bg-white text-xs" placeholder={t.method === "cash" ? "Note — optional" : "Reference — optional"}
                 value={t.reference || ""} onChange={(e) => setSplit(i, { reference: e.target.value })} />
          <button type="button" className="text-muted-foreground hover:text-red-600" aria-label="Remove this mode"
                  onClick={() => set({ split: split.filter((_, j) => j !== i) })}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      {r.payable > 0 && (
        <button type="button" onClick={addSplit} data-testid="payment-add-split"
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-navy underline-offset-2 hover:underline">
          <Plus className="h-3.5 w-3.5" /> Add another mode
        </button>
      )}
      {split.length > 0 && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          Leave Pay now blank and it takes what the other modes don't cover — {value.method || "cash"} ₹{amt(r.main)}.
        </p>
      )}
      <p className={`mt-2 text-xs ${r.balance > 0 ? "text-red-600" : "text-muted-foreground"}`} data-testid="payment-balance">
        {r.balance > 0 ? `Balance left on the bill: ₹${amt(r.balance)}` : "Bill fully paid"}
      </p>
    </div>
  );
}
